import type { FitnessWorkoutExercise, FitnessWorkoutSet, FitnessWorkoutState } from '@/lib/fitnessWorkoutController';
import { buildWorkoutSetSchedule } from './workoutSchedule';
import { validFitnessExerciseMetadata, validFitnessSetMetadata } from '../../../supabase/functions/_shared/fitnessWorkoutMetadata';
export interface WorkoutExerciseReplacement {
  name: string; muscle: string; timed?: boolean; defaultReps?: number; defaultWeight?: number;
  restSeconds?: number; gifUrl?: string; notes?: string;
}
const fail = (reason = 'invalid_fitness_exercise_replacement'): never => { throw new Error(reason); };
const finite = (value: unknown, min: number, max: number): value is number => typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
const integer = (value: unknown, min: number, max: number): value is number => finite(value, min, max) && Number.isSafeInteger(value);
const text = (value: unknown, max: number, empty = false): value is string => typeof value === 'string' && value.length <= max && (empty || !!value.trim()) && !value.includes('\u0000');
type Piece = { exercise: FitnessWorkoutExercise; sourceIndex: number; replacement?: boolean };
function withoutGroup(exercise: FitnessWorkoutExercise): FitnessWorkoutExercise {
  const result = { ...exercise }; delete result.groupId; delete result.groupType; return result;
}
function newGroupId(state: FitnessWorkoutState, indices: readonly number[], replacement: WorkoutExerciseReplacement): string {
  const seed = JSON.stringify([indices.map(index => [state.exercises[index].name, state.exercises[index].sets.filter(set => set.done).length]), replacement.name, replacement.muscle]);
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index++) hash = Math.imul(hash ^ seed.charCodeAt(index), 16777619) >>> 0;
  const prefix = 'replacement-' + hash.toString(36), used = new Set(state.exercises.map(exercise => exercise.groupId));
  let id = prefix, serial = 1; while (used.has(id)) id = prefix + '-' + serial++;
  return id;
}
function validate(state: FitnessWorkoutState, replacement: WorkoutExerciseReplacement): void {
  if (!state || !Array.isArray(state.exercises) || !integer(state.exercises.length, 1, 32) || state.phase === 'complete'
    || !integer(state.currentExerciseIndex, 0, state.exercises.length - 1) || !state.exerciseConfigs) return fail();
  if (!replacement || typeof replacement !== 'object' || Object.keys(replacement).some(key =>
    !['name', 'muscle', 'timed', 'defaultReps', 'defaultWeight', 'restSeconds', 'gifUrl', 'notes'].includes(key))
    || !text(replacement.name, 160) || !text(replacement.muscle, 80, true)
    || replacement.timed !== undefined && typeof replacement.timed !== 'boolean'
    || replacement.defaultReps !== undefined && !integer(replacement.defaultReps, 1, 100000)
    || replacement.defaultWeight !== undefined && !finite(replacement.defaultWeight, 0, 10000)
    || replacement.restSeconds !== undefined && !finite(replacement.restSeconds, 0, 86400)
    || replacement.gifUrl !== undefined && !text(replacement.gifUrl, 2048, true)
    || replacement.notes !== undefined && !text(replacement.notes, 2000, true)) return fail();
  buildWorkoutSetSchedule(state.exercises);
  for (let index = 0; index < state.exercises.length; index++) {
    const exercise = state.exercises[index], config = state.exerciseConfigs[index];
    if (!text(exercise.name, 160) || !text(exercise.muscle, 80, true) || !validFitnessExerciseMetadata(exercise as unknown as Record<string, unknown>)
      || !finite(exercise.defaultWeight, 0, 10000) || !integer(exercise.defaultReps, 1, 100000)
      || !config || config.sets !== exercise.sets.length || !finite(config.weight, 0, 10000) || !integer(config.reps, 1, 100000)) return fail();
    let pending = false;
    for (const set of exercise.sets) {
      if (!set || !finite(set.weight, 0, 10000) || !integer(set.reps, 1, 100000) || typeof set.done !== 'boolean'
        || !validFitnessSetMetadata(set as unknown as Record<string, unknown>) || set.done && pending) return fail();
      if (!set.done) pending = true;
    }
  }
  const current = state.exercises[state.currentExerciseIndex];
  if (!integer(state.currentSet, 1, current.sets.length)) return fail();
}
function newExercise(source: FitnessWorkoutExercise, remaining: readonly FitnessWorkoutSet[], choice: WorkoutExerciseReplacement): FitnessWorkoutExercise {
  const timed = choice.timed === true, changedUnit = timed !== (source.timed === true);
  const defaultReps = changedUnit ? choice.defaultReps ?? (timed ? 30 : 10) : source.defaultReps;
  const defaultWeight = choice.defaultWeight ?? 0;
  const sets = remaining.map(set => {
    const next = { ...set, done: false, weight: defaultWeight, reps: changedUnit ? defaultReps : set.reps };
    delete next.completedAt; delete next.rir;
    return next;
  });
  return { name: choice.name, muscle: choice.muscle, timed, defaultWeight, defaultReps,
    gifUrl: choice.gifUrl ?? '', restSeconds: choice.restSeconds ?? source.restSeconds ?? 60,
    ...(choice.notes !== undefined ? { notes: choice.notes } : {}), sets };
}
/** Pure same-session proposal. Completed identities/values never move to the new exercise. */
export function replaceCurrentWorkoutExercise(state: FitnessWorkoutState, replacement: WorkoutExerciseReplacement): FitnessWorkoutState {
  validate(state, replacement);
  const source = structuredClone(state), currentIndex = source.currentExerciseIndex, current = source.exercises[currentIndex];
  if (current.sets.every(set => set.done)) return fail('fitness_replacement_no_remaining_sets');
  let first = currentIndex, end = currentIndex + 1;
  if (current.groupId) {
    while (first > 0 && source.exercises[first - 1].groupId === current.groupId) first--;
    while (end < source.exercises.length && source.exercises[end].groupId === current.groupId) end++;
  }
  const indices = Array.from({ length: end - first }, (_, offset) => first + offset);
  const started = indices.some(index => source.exercises[index].sets.some(set => set.done));
  let pieces: Piece[], cursor: number;
  if (!started) {
    const next = newExercise(current, current.sets, replacement);
    if (current.groupId) Object.assign(next, { groupId: current.groupId, ...(current.groupType ? { groupType: current.groupType } : {}) });
    pieces = source.exercises.map((exercise, index) => ({ exercise: index === currentIndex ? next : exercise, sourceIndex: index, replacement: index === currentIndex }));
    // A never-started group keeps its declared order. The first member remains next.
    cursor = current.groupId ? first : currentIndex;
  } else {
    const prefix: Piece[] = indices.flatMap(index => {
      const exercise = source.exercises[index], done = exercise.sets.filter(set => set.done);
      return done.length ? [{ exercise: { ...withoutGroup(exercise), sets: done }, sourceIndex: index }] : [];
    });
    // Rotate a restarted group to the selected current member; no completed
    // round is repeated merely because its sets were split from the proposal.
    const rotated = current.groupId ? [...indices.filter(index => index >= currentIndex), ...indices.filter(index => index < currentIndex)] : indices;
    const groupId = current.groupId ? newGroupId(source, indices, replacement) : undefined;
    const tail: Piece[] = rotated.flatMap(index => {
      const exercise = source.exercises[index], remaining = exercise.sets.filter(set => !set.done);
      if (!remaining.length) return [];
      let next = index === currentIndex ? newExercise(exercise, remaining, replacement) : { ...exercise, sets: remaining };
      next = withoutGroup(next);
      if (groupId) Object.assign(next, { groupId, groupType: current.groupType ?? 'superset' });
      return [{ exercise: next, sourceIndex: index, replacement: index === currentIndex }];
    });
    pieces = [...source.exercises.slice(0, first).map((exercise, index) => ({ exercise, sourceIndex: index })),
      ...prefix, ...tail, ...source.exercises.slice(end).map((exercise, index) => ({ exercise, sourceIndex: end + index }))];
    cursor = first + prefix.length;
  }
  if (pieces.length > 32) return fail('fitness_replacement_exercise_limit');
  const exercises = pieces.map(piece => piece.exercise), schedule = buildWorkoutSetSchedule(exercises);
  if (!schedule.some(position => position.exerciseIndex === cursor && position.setNumber === 1)) return fail();
  const exerciseConfigs: FitnessWorkoutState['exerciseConfigs'] = {}, actualSetValues: FitnessWorkoutState['actualSetValues'] = {};
  pieces.forEach((piece, index) => {
    const exercise = piece.exercise, prior = source.exerciseConfigs[piece.sourceIndex];
    exerciseConfigs[index] = { weight: piece.replacement ? exercise.defaultWeight : prior.weight,
      reps: piece.replacement && (exercise.timed === true) !== (source.exercises[piece.sourceIndex].timed === true) ? exercise.defaultReps : prior.reps,
      sets: exercise.sets.length };
    actualSetValues[index] = exercise.sets.filter(set => set.done).map(set => ({ weight: set.weight, reps: set.reps }));
  });
  const selected = exercises[cursor], config = exerciseConfigs[cursor];
  return { ...source, exercises, exerciseConfigs, actualSetValues, currentExerciseIndex: cursor, currentSet: 1,
    phase: 'setup', editingWeight: config.weight, editingReps: config.reps, restDeadline: null, restDuration: selected.restSeconds ?? 60 };
}
